import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';
import { denormalize } from 'normalizr';
import { useStore, useDispatch, useSelector } from 'react-redux';
import {
  Route,
  Switch,
  useLocation,
  useRouteMatch,
} from 'react-router-dom/cjs/react-router-dom.min';

// Drag & drop
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';

// Icons
import { faRotateLeft, faRotateRight } from 'js/components/Icons/imports/solid';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';

// Actions
import {
  listRevisions,
  publishRevision,
} from 'js/actions/assessment-template-actions';
import {
  assignAnswerSetToQuestion,
  createAnswerSetChoice,
  createCustomAnswerSet,
  deleteAnswerSetChoice,
  moveAnswerSetChoiceAfter,
  moveAnswerSetChoiceBefore,
  updateAnswerSet,
} from 'js/components/Assessment/Editors/common/binding/answer-set-actions';
import { isLiveSentimentQuestion } from 'js/utils/sentiment-question';
import {
  addQuestionSet,
  copyQuestion,
  deleteElement,
  deletePage,
  moveElementAfter,
  moveElementBefore,
  moveElementToPage,
  movePageAfter,
  movePageBefore,
} from 'js/components/Assessment/Editors/common/binding/doc-utils';
import {
  addSentimentOption,
  addSentimentQuestion,
  deleteSentimentOption,
  moveSentimentOption,
  setQuestionDecoration,
} from 'js/components/Assessment/Editors/common/binding/sentiment-actions';

// Data & document plumbing
import { SCHEMAS } from 'js/api';
import { createBinding } from 'js/components/Assessment/Editors/common/binding';
import { useAssessment } from 'js/components/Assessment/Editors/common/binding/useAssessment';
import { useCollaborators } from 'js/components/Assessment/Editors/common/binding/collaborator-hooks';
import { AssessmentValidator } from 'js/components/Assessment/Editors/common/validation/validators';
import { AssessmentContext } from 'js/ydoc/docs/common/AssessmentContext';
import { DocumentProvider, useDoc, useProviders } from 'js/ydoc';
import { useNotify } from 'js/hooks/useNotify';
import useWindowDimensions from 'js/utils/window-dimensions-hook';
import { revisionFor } from 'js/utils/assessment-draft';

// Shared editor UI
import { AnswerSetContextProvider } from 'js/components/Assessment/Editors/common/components/AnswerSets/AnswerSetContext';
import EditorPreview from 'js/components/Assessment/Editors/common/Preview/EditorPreview';
import EditorErrors from 'js/components/Assessment/Editors/common/components/EditorErrors';
import AssessmentType from 'js/components/Assessment/Editors/common/Tools/MetadataAndHistoryTool/AssessmentType';

// Live editor UI
import { QuestionSet } from './QuestionSets/QuestionSet';
import { Tools } from './Tools/Tools';

import { Icon, ReplaceLink } from 'js/components';
import CollaboratorList from 'js/components/CollaboratorList/CollaboratorList';
import AsyncButton from 'js/components/Forms/AsyncButton';

import styles from 'js/components/Assessment/Editors/common/EditorLayout.module.scss';

const validator = new AssessmentValidator();

function questionTypeFromDoc(ydoc, questionId) {
  return { type: ydoc.getMap('elements').get(questionId)?.get('type') };
}

export function Editor({ assessmentDocTemplate }) {
  const token = useSelector((state) => state.identity.token);

  if (!token) {
    return null;
  }

  return (
    <DocumentProvider
      channelProviderToken={token}
      channelProviderTopic={`crdt:assessment-doc-template:${assessmentDocTemplate.crdt_doc_id}`}
    >
      <EditorInterface docTemplateId={assessmentDocTemplate.id} />
    </DocumentProvider>
  );
}

/**
 * `headerAction` replaces the Publish button and `backTo` the back link, for
 * editing a customized-assessment draft. A draft has no `docTemplateId`, so
 * no revisions either.
 */
export function EditorInterface({ docTemplateId, backTo, headerAction }) {
  const ydoc = useDoc();
  const providers = useProviders();
  const notify = useNotify();
  const dispatch = useDispatch();
  const store = useStore();
  const match = useRouteMatch();
  const location = useLocation();
  const windowDims = useWindowDimensions();
  const mainPaneRef = useRef(null);

  const { assessment, binding } = useAssessment(createBinding);
  const collaborators = useCollaborators();

  const validationErrors = validator.validateAssessment(assessment);
  const warnings = useMemo(
    () => validator.collectWarnings(assessment),
    [assessment]
  );

  const [showModalSettings, setShowModalSettings] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [revisionPublishedAt, setRevisionPublishedAt] = useState(null);

  const currentWorkspaceId = useSelector((state) => state.currentWorkspaceId);
  const revisionsQuery = useSelector(
    (state) => state.queries[`${docTemplateId}-revisions`]
  );

  const isShowPreview = useMemo(
    () => location.pathname.includes('/edit/preview'),
    [location]
  );
  const useModalSettings = useMemo(
    () => windowDims.width <= 1050,
    [windowDims]
  );

  const awareness = useMemo(
    () => providers.channelProvider?.awareness,
    [providers]
  );

  const undo = useCallback(
    () => binding.undoManager.undo(),
    [binding.undoManager]
  );
  const redo = useCallback(
    () => binding.undoManager.redo(),
    [binding.undoManager]
  );

  const handleQuestionFieldChange = useCallback(
    (id, field, value) => {
      ydoc.getMap('elements')?.get(id)?.set(field, value);
    },
    [ydoc]
  );

  const handlePageChange = useCallback(
    (id, field, value) => {
      ydoc.getMap('pages')?.get(id)?.set(field, value);
    },
    [ydoc]
  );

  const handleFieldFocusChange = useCallback(
    (type, parentId, field, focused) => {
      awareness?.setLocalStateField(
        'focusedField',
        focused ? { parentType: type, parentId, field } : null
      );
    },
    [awareness]
  );

  const handleAddQuestion = useCallback(
    (questionSetId) => {
      addSentimentQuestion(ydoc, questionSetId, 'Enter the question text');
    },
    [ydoc]
  );

  const handleCopyQuestion = useCallback(
    (question) => copyQuestion(ydoc, question.guid),
    [ydoc]
  );

  const handleDeleteQuestion = useCallback(
    (question) => {
      deleteElement(ydoc, question.guid);
      notify({ type: 'info', message: 'Question deleted' });
    },
    [ydoc, notify]
  );

  const handleAddQuestionSet = useCallback(
    () => addQuestionSet(ydoc, 'New question set'),
    [ydoc]
  );

  const handleDeleteQuestionSet = useCallback(
    (set) => {
      deletePage(ydoc, set.guid);
      notify({ type: 'info', message: 'Question set deleted' });
    },
    [ydoc, notify]
  );

  const handleAnswerSetChoiceAdd = useCallback(
    (questionId, answerSetId, choiceProps) => {
      if (answerSetId == null) {
        createCustomAnswerSet(ydoc, questionId, {
          choices: { 1: choiceProps },
        });
        return;
      }

      if (isLiveSentimentQuestion(questionTypeFromDoc(ydoc, questionId))) {
        addSentimentOption(ydoc, answerSetId, choiceProps);
        return;
      }

      createAnswerSetChoice(ydoc, answerSetId, choiceProps);
    },
    [ydoc]
  );

  const handleAnswerSetChoiceDelete = useCallback(
    (answerSetId, choiceId, questionId) => {
      if (isLiveSentimentQuestion(questionTypeFromDoc(ydoc, questionId))) {
        deleteSentimentOption(ydoc, answerSetId, choiceId);
        return;
      }

      deleteAnswerSetChoice(ydoc, answerSetId, choiceId);
    },
    [ydoc]
  );

  const handleAnswerSetChoiceMove = useCallback(
    (answerSetId, sourceChoiceId, targetChoiceId, placeAfter, questionId) => {
      if (isLiveSentimentQuestion(questionTypeFromDoc(ydoc, questionId))) {
        moveSentimentOption(
          ydoc,
          answerSetId,
          sourceChoiceId,
          targetChoiceId,
          placeAfter
        );
        return;
      }

      if (placeAfter) {
        moveAnswerSetChoiceAfter(
          ydoc,
          answerSetId,
          sourceChoiceId,
          targetChoiceId
        );
      } else {
        moveAnswerSetChoiceBefore(
          ydoc,
          answerSetId,
          sourceChoiceId,
          targetChoiceId
        );
      }
    },
    [ydoc]
  );

  const handleAnswerSetChoiceUpdate = useCallback(
    (answerSetId, choiceId, updates) => {
      ydoc.transact(() => {
        const choice = ydoc
          .getMap('answerSets')
          .get(answerSetId)
          ?.get('choices')
          ?.get(choiceId);

        Object.entries(updates).forEach(([key, value]) =>
          choice?.set(key, value)
        );
      });
    },
    [ydoc]
  );

  const handleAnswerSetChange = useCallback(
    (questionId, answerSet) => {
      assignAnswerSetToQuestion(ydoc, questionId, answerSet);
    },
    [ydoc]
  );

  const handleAnswerSetFieldChange = useCallback(
    (answerSetId, fields) => {
      updateAnswerSet(ydoc, answerSetId, fields);
    },
    [ydoc]
  );

  const handleDecorationChange = useCallback(
    (questionId, decoration) => {
      setQuestionDecoration(ydoc, questionId, decoration);
    },
    [ydoc]
  );

  const answerSetContext = useMemo(
    () => ({
      onDecorationChange: handleDecorationChange,
    }),
    [handleDecorationChange]
  );

  useEffect(() => {
    return monitorForElements({
      onDrop({ source, location }) {
        if (location.current.dropTargets.length <= 0) {
          return;
        }

        const target = location.current.dropTargets[0];
        const closestEdge = extractClosestEdge(target.data);

        if (source.data.question) {
          if (target.data.questionSet) {
            moveElementToPage(
              ydoc,
              source.data.question.guid,
              target.data.questionSet.guid
            );
          } else if (closestEdge === 'top') {
            moveElementBefore(
              ydoc,
              source.data.question.guid,
              target.data.question.guid
            );
          } else {
            moveElementAfter(
              ydoc,
              source.data.question.guid,
              target.data.question.guid
            );
          }
        } else if (source.data.questionSet) {
          if (closestEdge === 'top') {
            movePageBefore(
              ydoc,
              source.data.questionSet.guid,
              target.data.questionSet.guid
            );
          } else {
            movePageAfter(
              ydoc,
              source.data.questionSet.guid,
              target.data.questionSet.guid
            );
          }
        }
      },
    });
  }, [ydoc]);

  useEffect(() => {
    const mainPaneEl = mainPaneRef.current;
    if (!mainPaneEl) throw new Error('No element found');
    return autoScrollForElements({ element: mainPaneEl });
  }, []);

  useEffect(() => {
    if (!useModalSettings) {
      setShowModalSettings(false);
    }
  }, [useModalSettings]);

  useEffect(() => {
    if (revisionsQuery?.isLoading === false) {
      setRevisions(
        denormalize(
          revisionsQuery?.result || [],
          SCHEMAS.ASSESSMENT_DOC_TEMPLATE_REVISION_ARRAY,
          store.getState().entities
        )
      );
    }
  }, [
    store,
    revisionsQuery,
    revisionsQuery?.isLoading,
    revisionsQuery?.result,
  ]);

  useEffect(() => {
    if (!docTemplateId) {
      return;
    }

    listRevisions(docTemplateId, `${docTemplateId}-revisions`)(
      dispatch,
      store.getState
    );
  }, [docTemplateId, revisionPublishedAt, dispatch, store]);

  const handlePublish = useCallback(() => {
    const revision = revisionFor(assessment, { doc: ydoc });

    return publishRevision(docTemplateId, revision)(dispatch, store.getState)
      .then(() => {
        setRevisionPublishedAt(Date.now());
        notify({
          type: 'success',
          message: `Revision ${revision.version_id} published!`,
        });
      })
      .catch(() => {
        notify({ type: 'danger', message: 'Failed to publish revision' });
      });
  }, [docTemplateId, ydoc, assessment, dispatch, store, notify]);

  return (
    <AssessmentContext.Provider value={assessment}>
      <AnswerSetContextProvider value={answerSetContext}>
        <div
          className={classNames(styles.editor, {
            [styles.preview]: isShowPreview,
          })}
        >
          <div className={styles.mainHeader}>
            <div className={styles.nav}>
              <ReplaceLink
                to={backTo ?? `/w/${currentWorkspaceId}/assessments`}
              >
                <Icon icon={faChevronLeft} />
              </ReplaceLink>
              <ReplaceLink
                to={match.url.replace('/preview', '')}
                className={classNames({ [styles.active]: !isShowPreview })}
              >
                Assessment Editor
              </ReplaceLink>
              <ReplaceLink
                to={`${match.url}/preview`}
                className={classNames({ [styles.active]: isShowPreview })}
              >
                Preview
              </ReplaceLink>
            </div>
            <div className={styles.headerActions}>
              <div
                className={classNames(styles.grouping, styles.collaborators)}
              >
                <CollaboratorList collaborators={collaborators} />
              </div>
              {!isShowPreview && (
                <>
                  <div className={styles.grouping}>
                    <button
                      className={styles.iconButton}
                      onClick={undo}
                      disabled={!binding.undoManager.canUndo()}
                    >
                      <i>
                        <Icon icon={faRotateLeft} />
                      </i>
                    </button>
                    <button
                      className={styles.iconButton}
                      onClick={redo}
                      disabled={!binding.undoManager.canRedo()}
                    >
                      <i>
                        <Icon icon={faRotateRight} />
                      </i>
                    </button>
                  </div>
                  <div className={styles.grouping}>
                    <AssessmentType type={assessment?.meta?.type} />
                  </div>
                  <div className={styles.grouping}>
                    <EditorErrors
                      errors={validationErrors}
                      warnings={warnings}
                    />
                  </div>
                  <div className={styles.grouping}>
                    {headerAction ? (
                      headerAction({
                        assessment,
                        hasErrors: validationErrors.length > 0,
                      })
                    ) : (
                      <AsyncButton
                        color="primary"
                        onClick={handlePublish}
                        disabled={validationErrors.length > 0}
                      >
                        Publish
                      </AsyncButton>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          <div
            className={classNames(styles.mainPane, {
              [styles.noscroll]: useModalSettings,
            })}
            ref={mainPaneRef}
          >
            <Switch>
              <Route path={`${match.path}/preview`}>
                {assessment && (
                  <EditorPreview
                    assessmentType="live"
                    assessment={assessment}
                  />
                )}
              </Route>
              <Route>
                {useModalSettings && showModalSettings && (
                  <div
                    className={styles.mainPaneOverlay}
                    onClick={() => setShowModalSettings(false)}
                  ></div>
                )}
                <div className={styles.assessment}>
                  {assessment?.pages?.map((qs, i) => (
                    <QuestionSet
                      index={i}
                      questionSet={qs}
                      key={qs.guid}
                      onFieldChange={handlePageChange}
                      onDelete={handleDeleteQuestionSet}
                      onQuestionFieldChange={handleQuestionFieldChange}
                      onFieldFocus={handleFieldFocusChange}
                      onDeleteQuestion={handleDeleteQuestion}
                      onAddQuestion={handleAddQuestion}
                      onCopyQuestion={handleCopyQuestion}
                      onAnswerSetChoiceAdd={handleAnswerSetChoiceAdd}
                      onAnswerSetChoiceDelete={handleAnswerSetChoiceDelete}
                      onAnswerSetChoiceUpdate={handleAnswerSetChoiceUpdate}
                      onAnswerSetChoiceMove={handleAnswerSetChoiceMove}
                      onAnswerSetChange={handleAnswerSetChange}
                      onAnswerSetFieldChange={handleAnswerSetFieldChange}
                    />
                  ))}
                  <div className={styles.addButtonContainer}>
                    <button
                      className={styles.addButton}
                      onClick={handleAddQuestionSet}
                    >
                      <i>
                        <Icon icon="plus" className="btn-icon-left" />
                      </i>
                      Add a question set
                    </button>
                  </div>
                </div>
              </Route>
            </Switch>
          </div>
          {!isShowPreview && (!useModalSettings || showModalSettings) && (
            <div className={styles.settingsPane}>
              <div className={styles.toolPane}>
                <Tools
                  docTemplateId={docTemplateId}
                  assessment={assessment}
                  revisions={revisions}
                  revisionsQueryIsLoading={revisionsQuery?.isLoading === true}
                />
              </div>
            </div>
          )}
        </div>
      </AnswerSetContextProvider>
    </AssessmentContext.Provider>
  );
}

export default Editor;
